import { useTranslation } from "react-i18next"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  AppliedSegment,
  AssignmentSegmentOption,
} from "@/types/mission-assign"

/**
 * MissionAssignSegmentControl — segment selector + applied-segment
 * summary chip (UX Spec §3).
 *
 * Emits `onChange(id | null)`. Parent owns the URL via
 * `useAssignmentUrl#setSegment`.
 */
interface MissionAssignSegmentControlProps {
  segments: AssignmentSegmentOption[]
  appliedSegment: AppliedSegment | null
  onChange: (id: number | null) => void
  testId?: string
}

export function MissionAssignSegmentControl({
  segments,
  appliedSegment,
  onChange,
  testId = "mission-assign-segment-control",
}: MissionAssignSegmentControlProps) {
  const { t } = useTranslation()

  if (segments.length === 0) {
    return (
      <p
        data-testid={`${testId}-empty`}
        className="text-muted-foreground text-sm"
      >
        {t("missions.assign.segment.no_segments")}
      </p>
    )
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <Label htmlFor="mission-assign-segment-select">
        {t("missions.assign.segment.select_label")}
      </Label>
      <Select
        items={segments.map((segment) => ({
          value: String(segment.id),
          label: segment.name,
        }))}
        value={appliedSegment ? String(appliedSegment.id) : undefined}
        onValueChange={(value) => onChange(value ? Number(value) : null)}
      >
        <SelectTrigger
          id="mission-assign-segment-select"
          data-testid="mission-assign-segment-select"
        >
          <SelectValue
            placeholder={t("missions.assign.segment.select_placeholder")}
          />
        </SelectTrigger>
        <SelectContent>
          {segments.map((segment) => (
            <SelectItem key={segment.id} value={String(segment.id)}>
              <span className="flex items-center justify-between gap-3">
                <span>{segment.name}</span>
                <span className="text-muted-foreground text-xs">
                  {segment.member_count}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {appliedSegment && <AppliedRulesChip segment={appliedSegment} />}
    </div>
  )
}

function AppliedRulesChip({ segment }: { segment: AppliedSegment }) {
  const { t } = useTranslation()
  const summary = useFormattedRulesSummary(segment.rules)

  return (
    <p
      data-testid="mission-assign-segment-applied-chip"
      className="text-muted-foreground text-xs"
    >
      {t("missions.assign.segment.rules_summary", { summary })}
    </p>
  )
}

// Compose the rules digest from per-part i18n keys so EN strings don't
// leak into FR. Each part is rendered via t() and the joined chain is
// returned to the chip's outer t() call.
function useFormattedRulesSummary(rules: Record<string, unknown>): string {
  const { t } = useTranslation()
  const parts: string[] = []

  const tags = rules.tags
  if (Array.isArray(tags) && tags.length > 0) {
    parts.push(
      t("missions.assign.segment.rules_part.tags", {
        tags: tags.join(", "),
      }),
    )
  }
  if (typeof rules.location === "string" && rules.location) {
    parts.push(
      t("missions.assign.segment.rules_part.location", {
        location: rules.location,
      }),
    )
  }
  if (typeof rules.followers_min === "number") {
    parts.push(
      t("missions.assign.segment.rules_part.followers_min", {
        count: rules.followers_min,
      }),
    )
  }
  if (typeof rules.followers_max === "number") {
    parts.push(
      t("missions.assign.segment.rules_part.followers_max", {
        count: rules.followers_max,
      }),
    )
  }

  return parts.length > 0
    ? parts.join(t("missions.assign.segment.rules_separator"))
    : t("missions.assign.segment.rules_empty")
}
